import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { DrawSVGPlugin } from "gsap/DrawSVGPlugin";
import { MotionPathPlugin } from "gsap/MotionPathPlugin";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import { initHeroStudio } from "./hero-studio";
import { initDemoStudio } from "./demo-studio";
import { initProjectFinder, initProjectEnquiry, initContactHandoff } from "./project-finder.mjs";
import { redirectLegacySection, initContactDraftGuard } from './site-navigation.mjs';

gsap.registerPlugin(ScrollTrigger, DrawSVGPlugin, MotionPathPlugin, ScrollToPlugin);

const qsa = (selector, root = document) => Array.from(root.querySelectorAll(selector));

function initHeader() {
  const header = document.querySelector("[data-site-header]");
  const toggle = document.querySelector("[data-menu-toggle]");
  const panel = document.querySelector("[data-header-panel]");

  if (header) {
    const updateHeader = () => header.classList.toggle("is-scrolled", window.scrollY > 24);
    updateHeader();
    ScrollTrigger.create({
      start: 0,
      end: "max",
      onUpdate: updateHeader,
    });
  }

  if (toggle && panel) {
    const closeMenu = () => {
      toggle.setAttribute("aria-expanded", "false");
      panel.classList.remove("is-open");
      document.body.classList.remove("menu-open");
    };

    toggle.addEventListener("click", () => {
      const willOpen = toggle.getAttribute("aria-expanded") !== "true";
      toggle.setAttribute("aria-expanded", String(willOpen));
      panel.classList.toggle("is-open", willOpen);
      document.body.classList.toggle("menu-open", willOpen);
    });

    qsa("a", panel).forEach((link) => link.addEventListener("click", closeMenu));
    window.addEventListener("resize", () => {
      if (window.innerWidth >= 1100) closeMenu();
    });
  }
}

function initSmoothScroll() {
  qsa("[data-scroll]").forEach((link) => {
    link.addEventListener("click", (event) => {
      const url = new URL(link.href, window.location.href);
      const sameDocument =
        url.origin === window.location.origin &&
        url.pathname === window.location.pathname &&
        url.search === window.location.search;

      if (!sameDocument || !url.hash) return;

      const target = document.querySelector(url.hash);
      if (!target) return;

      event.preventDefault();
      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      gsap.to(window, {
        duration: reduceMotion ? 0 : 0.85,
        ease: "power3.inOut",
        scrollTo: { y: target, offsetY: 78, autoKill: true },
        onComplete: () => window.history.replaceState(null, "", url.hash),
      });
    });
  });
}

function initServices() {
  const tabs = qsa("[data-service-tab]");
  const panels = qsa("[data-service-panel]");
  if (!tabs.length || !panels.length) return;

  let current = 0;

  const activate = (nextIndex) => {
    if (nextIndex === current || !panels[nextIndex]) return;

    const oldPanel = panels[current];
    const nextPanel = panels[nextIndex];

    tabs.forEach((tab, index) => {
      const active = index === nextIndex;
      tab.classList.toggle("is-active", active);
      tab.setAttribute("aria-expanded", String(active));
    });

    gsap.to(oldPanel, {
      autoAlpha: 0,
      y: -12,
      duration: 0.22,
      ease: "power2.in",
      onComplete: () => {
        oldPanel.hidden = true;
        oldPanel.classList.remove("is-active");
        nextPanel.hidden = false;
        nextPanel.classList.add("is-active");
        gsap.fromTo(
          nextPanel,
          { autoAlpha: 0, y: 18 },
          { autoAlpha: 1, y: 0, duration: 0.48, ease: "power3.out", overwrite: true }
        );
        ScrollTrigger.refresh();
      },
    });

    current = nextIndex;
  };

  tabs.forEach((tab, index) => tab.addEventListener("click", () => activate(index)));
}

function initMotion() {
  const media = gsap.matchMedia();

  media.add(
    {
      desktop: "(min-width: 760px)",
      mobile: "(max-width: 759px)",
      wide: "(min-width: 1100px)",
      tall: "(min-height: 820px)",
      reduceMotion: "(prefers-reduced-motion: reduce)",
    },
    (context) => {
      const { desktop, reduceMotion, wide } = context.conditions;
      const revealElements = qsa(".reveal");
      const paths = qsa("[data-signal-path]");
      let clearProcessState = () => {};
      const cleanupHero = initHeroStudio(context.conditions);

      if (reduceMotion) {
        if (revealElements.length) gsap.set(revealElements, { autoAlpha: 1, y: 0, clearProps: "transform" });
        if (paths.length) gsap.set(paths, { drawSVG: "0% 100%" });
        return cleanupHero;
      }

      if (revealElements.length) {
        gsap.set(revealElements, { autoAlpha: 0, y: 28 });
        ScrollTrigger.batch(revealElements, {
          start: "top 88%",
          once: true,
          interval: 0.08,
          batchMax: desktop ? 4 : 2,
          onEnter: (batch) => gsap.to(batch, { autoAlpha: 1, y: 0, duration: 0.66, stagger: 0.1, ease: "power3.out", overwrite: true }),
        });
      }

      paths.forEach((path) => {
        const svg = path.closest("svg");
        const dot = svg?.querySelector("[data-signal-dot]");
        const section = svg?.closest("section") || svg;

        gsap.fromTo(
          path,
          { drawSVG: "0% 0%" },
          {
            drawSVG: "0% 100%",
            ease: "none",
            scrollTrigger: { trigger: section, start: "top 82%", end: "bottom 34%", scrub: desktop ? 0.8 : 0.35 },
          }
        );

        if (dot) {
          gsap.to(dot, {
            motionPath: { path, align: path, alignOrigin: [0.5, 0.5] },
            ease: "none",
            scrollTrigger: { trigger: section, start: "top 82%", end: "bottom 34%", scrub: desktop ? 0.9 : 0.4 },
          });
        }
      });

      const processRoot = document.querySelector("[data-process]");
      const processMap = processRoot?.querySelector("[data-process-map]");
      if (processRoot && processMap) {
        const processSteps = qsa("[data-process-step]", processRoot);
        const processScenes = qsa("[data-process-scene]", processRoot);
        const processVisualLabel = processRoot.querySelector("[data-process-visual-label]");
        const processVisualIndex = processRoot.querySelector("[data-process-visual-index]");
        const processRoute = processMap.querySelector("[data-process-route]");
        const processPulse = processMap.querySelector("[data-process-pulse]");

        if (processSteps.length) {
          const segmentDuration = desktop ? 3.2 : 3.4;
          const cycleDuration = segmentDuration * processSteps.length;

          clearProcessState = () => {
            delete processRoot.dataset.processStage;
            processSteps.forEach((step) => {
              step.classList.remove("is-active");
              delete step.dataset.processActive;
              const progress = step.querySelector(".process-step__progress");
              if (progress) gsap.set(progress, { scaleX: 0, autoAlpha: 0 });
            });
            processScenes.forEach((scene) => {
              scene.classList.remove("is-active");
              scene.setAttribute("aria-hidden", "true");
            });
            if (processScenes.length) gsap.set(processScenes, { autoAlpha: 0, y: 14, scale: 0.985 });
          };

          const activateProcessStep = (index) => {
            clearProcessState();
            const step = processSteps[index];
            if (!step) return;
            const scene = processScenes[index];
            const title = step.querySelector("h3")?.textContent?.trim() || "";
            step.classList.add("is-active");
            step.dataset.processActive = "true";
            processRoot.dataset.processStage = String(index + 1);
            if (scene) {
              scene.classList.add("is-active");
              scene.setAttribute("aria-hidden", "false");
            }
            if (processVisualLabel) processVisualLabel.textContent = title;
            if (processVisualIndex) processVisualIndex.textContent = String(index + 1).padStart(2, "0");
          };

          const processTimeline = gsap.timeline({
            paused: true,
            repeat: -1,
            repeatDelay: 0.3,
            defaults: { ease: "power2.out" },
          });

          if (processScenes.length) gsap.set(processScenes, { autoAlpha: 0, y: 14, scale: 0.985 });

          if (wide && processRoute && processPulse) {
            gsap.set(processPulse, { autoAlpha: 1 });
            processTimeline
              .to(
                processPulse,
                {
                  motionPath: { path: processRoute, align: processRoute, alignOrigin: [0.5, 0.5] },
                  duration: cycleDuration,
                  ease: "none",
                },
                0
              )
              .fromTo(processPulse, { autoAlpha: 0.2 }, { autoAlpha: 1, duration: 0.28 }, 0)
              .to(processPulse, { autoAlpha: 0, duration: 0.28 }, cycleDuration - 0.28);
          }

          processSteps.forEach((step, index) => {
            const position = index * segmentDuration;
            const progress = step.querySelector(".process-step__progress");
            const icon = step.querySelector(".process-step__icon");
            const node = step.querySelector(".process-step__node span");
            const number = step.querySelector(".process-step__number");
            const scene = processScenes[index];

            processTimeline.call(activateProcessStep, [index], position);
            if (scene) {
              const sceneElements = qsa("[data-scene-element]", scene);
              processTimeline.fromTo(
                scene,
                { autoAlpha: 0, y: 14, scale: 0.985 },
                { autoAlpha: 1, y: 0, scale: 1, duration: 0.56, ease: "power3.out", immediateRender: false },
                position
              );
              if (sceneElements.length) {
                processTimeline.fromTo(
                  sceneElements,
                  { autoAlpha: 0, y: 12 },
                  { autoAlpha: 1, y: 0, duration: 0.52, stagger: 0.07, ease: "power3.out", immediateRender: false },
                  position + 0.12
                );
              }

              const discoverRoute = scene.querySelector("[data-discover-route]");
              const discoverScanner = scene.querySelector("[data-discover-scanner]");
              const designCursor = scene.querySelector(".design-cursor");
              const codeLines = qsa("[data-code-line]", scene);
              const buildOrbit = scene.querySelector("[data-build-orbit]");
              const improvePath = scene.querySelector("[data-improve-path]");
              const improveRing = scene.querySelector("[data-improve-ring]");

              if (discoverRoute) {
                processTimeline.fromTo(discoverRoute, { drawSVG: "0% 0%" }, { drawSVG: "0% 100%", duration: segmentDuration * 0.74, ease: "power2.inOut", immediateRender: false }, position + 0.08);
              }
              if (discoverScanner) {
                processTimeline.fromTo(discoverScanner, { rotation: -20, scale: 0.72 }, { rotation: 230, scale: 1.04, duration: segmentDuration * 0.78, ease: "power2.inOut", immediateRender: false }, position + 0.1);
              }
              if (designCursor) {
                processTimeline.fromTo(designCursor, { x: -24, y: 20, rotation: -8 }, { x: 22, y: -18, rotation: 7, duration: segmentDuration * 0.72, ease: "power2.inOut", immediateRender: false }, position + 0.18);
              }
              if (codeLines.length) {
                processTimeline.fromTo(codeLines, { scaleX: 0, autoAlpha: 0.35 }, { scaleX: 1, autoAlpha: 1, duration: 0.48, stagger: 0.15, ease: "power2.out", immediateRender: false }, position + 0.18);
              }
              if (buildOrbit) {
                processTimeline.fromTo(buildOrbit, { rotation: -25, scale: 0.82 }, { rotation: 320, scale: 1, duration: segmentDuration * 0.8, ease: "none", immediateRender: false }, position + 0.08);
              }
              if (improvePath) {
                processTimeline.fromTo(improvePath, { drawSVG: "0% 0%" }, { drawSVG: "0% 100%", duration: segmentDuration * 0.72, ease: "power2.inOut", immediateRender: false }, position + 0.14);
              }
              if (improveRing) {
                processTimeline.fromTo(improveRing, { rotation: -40, scale: 0.78 }, { rotation: 300, scale: 1, duration: segmentDuration * 0.76, ease: "power2.inOut", immediateRender: false }, position + 0.12);
              }
            }
            if (progress) {
              processTimeline.fromTo(
                progress,
                { scaleX: 0, autoAlpha: 0.3 },
                { scaleX: 1, autoAlpha: 1, duration: segmentDuration * 0.82, ease: "power2.out" },
                position
              );
            }
            if (icon) {
              processTimeline
                .to(icon, { y: -7, scale: 1.06, duration: 0.34 }, position)
                .to(icon, { y: 0, scale: 1, duration: 0.58, ease: "power2.inOut" }, position + 0.46);
            }
            if (node) {
              processTimeline
                .to(node, { scale: 2.1, duration: 0.24 }, position)
                .to(node, { scale: 1, duration: 0.52, ease: "power2.inOut" }, position + 0.24);
            }
            if (number) {
              processTimeline.fromTo(number, { y: 5, autoAlpha: 0.72 }, { y: 0, autoAlpha: 1, duration: 0.4 }, position);
            }
          });

          ScrollTrigger.create({
            id: "process-autoplay",
            trigger: processRoot,
            start: "top 72%",
            end: "bottom 16%",
            onEnter: () => processTimeline.restart(),
            onEnterBack: () => processTimeline.restart(),
            onLeave: () => {
              processTimeline.pause();
              clearProcessState();
            },
            onLeaveBack: () => {
              processTimeline.pause();
              clearProcessState();
            },
          });
        }
      }

      if (desktop && document.querySelector(".company__visual")) {
        gsap.to(".company__visual", {
          y: -55,
          ease: "none",
          scrollTrigger: { trigger: ".company", start: "top bottom", end: "bottom top", scrub: 1 },
        });
      }

      return () => { cleanupHero(); clearProcessState(); };
    }
  );

  window.addEventListener("load", () => ScrollTrigger.refresh(), { once: true });
}

document.addEventListener("DOMContentLoaded", () => {
  window.addEventListener('hashchange', redirectLegacySection);
  if (redirectLegacySection()) return;
  initHeader();
  initSmoothScroll();
  initServices();
  initProjectFinder(() => ScrollTrigger.refresh());
  initContactHandoff(() => ScrollTrigger.refresh());
  initProjectEnquiry(() => ScrollTrigger.refresh());
  initContactDraftGuard();
  initMotion();
  initDemoStudio(() => ScrollTrigger.refresh());
  if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    const entering = qsa('[data-page-enter]');
    if (entering.length) gsap.from(entering, { autoAlpha: 0, y: 14, duration: .48, stagger: .07, ease: 'power2.out', clearProps: 'transform,opacity,visibility' });
  }
});
